import React, { useState } from 'react';
import { XIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
interface ChangePasswordBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => void;
}
export function ChangePasswordBottomSheet({
  isOpen,
  onClose,
  onSubmit
}: ChangePasswordBottomSheetProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit({
        currentPassword,
        newPassword,
        confirmPassword
      });
    }
    handleClose();
  };
  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };
  if (!isOpen) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity" onClick={handleClose} data-id="element-235" data-mp="1mbvme9fa9l65" />
      {/* Bottom Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-[101] bg-white rounded-t-3xl max-h-[90vh] overflow-hidden animate-slide-up" data-id="element-236" data-mp="2e31kkkbg18ss">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2" data-id="element-237" data-mp="29ytwmpct7wvm">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-238" data-mp="25122rpslu0os" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200" data-id="element-239" data-mp="201p7qhrkxnb4">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-240" data-mp="tmybirdeiqnf">Change Password</h3>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-241" data-mp="onv635tieiex">
            <XIcon className="w-5 h-5 text-gray-500" data-id="element-242" data-mp="q6255es9cbrg" />
          </button>
        </div>
        {/* Content */}
        <div className="px-5 py-6 space-y-5" data-id="element-243" data-mp="v32f40s89ynu">
          {/* Current Password */}
          <div data-id="element-244" data-mp="h3asjbnnb9y8">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-245" data-mp="h3syj00412jc">
              Current Password
            </label>
            <div className="relative" data-id="element-246" data-mp="7e9ehr0nv2mw">
              <input type={showCurrentPassword ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" placeholder="Enter current password" data-id="element-247" data-mp="7d831lfqge4n" />
              <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-248" data-mp="3klkwcv0xggpg">
                {showCurrentPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-249" data-mp="310ketv688mpr" /> : <EyeIcon className="w-5 h-5" data-id="element-250" data-mp="3fxbf06nq62gf" />}
              </button>
            </div>
          </div>
          {/* New Password */}
          <div data-id="element-251" data-mp="3azjnhdqzsicr">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-252" data-mp="3owoqf4vet13s">
              New Password
            </label>
            <div className="relative" data-id="element-253" data-mp="3jyn5eeefarpo">
              <input type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" placeholder="Enter new password" data-id="element-254" data-mp="3jvsogwumbp10" />
              <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-255" data-mp="7eng05txxg8l">
                {showNewPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-256" data-mp="2apsc6mti0obv" /> : <EyeIcon className="w-5 h-5" data-id="element-257" data-mp="2fms8w74gkeqb" />}
              </button>
            </div>
          </div>
          {/* Confirm New Password */}
          <div data-id="element-258" data-mp="1mz66h26yvg51">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-259" data-mp="1mywh9gtqxd0e">
              Confirm New Password
            </label>
            <div className="relative" data-id="element-260" data-mp="1rwxydw7m3298">
              <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" placeholder="Confirm new password" data-id="element-261" data-mp="1w04dltcpc3ru" />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-262" data-mp="rq2jgp6bhouo">
                {showConfirmPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-263" data-mp="d1y1jnf39hcy" /> : <EyeIcon className="w-5 h-5" data-id="element-264" data-mp="82bll67vln0g" />}
              </button>
            </div>
          </div>
        </div>
        {/* Footer Actions */}
        <div className="flex gap-3 px-5 py-4 border-t border-gray-200 bg-white sticky bottom-0" data-id="element-265" data-mp="419ujco44h9k">
          <button onClick={handleClose} className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors" data-id="element-266" data-mp="3v8m4b6mo1sfg">
            Cancel
          </button>
          <button onClick={handleSubmit} className="flex-1 px-4 py-3 bg-[#1E2B55] text-white font-semibold rounded-xl hover:bg-[#162041] transition-colors" data-id="element-267" data-mp="2ckb39lmf58vd">
            Update Password
          </button>
        </div>
      </div>
      <style data-id="element-268" data-mp="23lg06ieo8ewa">{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>;
}